import { useCallback, useEffect, useRef } from 'react'
import { PaymentMethod } from 'models/ChargeIntent'
import { getAnalyticsInstance } from 'modules/analytics/analytics'
import {
  getPaymentMethodConfig,
  useActivePaymentMethodConfigs,
} from '../utils/use-payment-method-configs'
import useTrackUIElementEvent from 'modules/analytics/hooks/useTrackUIElementEvent'
import mParticleSdk from '@mparticle/web-sdk'
import useOrder from '../../../../order/state/order/use-order'
import { PaymentMethodConfig } from '../utils/payment-method-configs'

export const usePaymentMethodSelection = () => {
  const order = useOrder()
  const analytics = getAnalyticsInstance()
  const hasInitializedRef = useRef(false)
  const walletType = order.stripePaymentRequest.walletType
  const currency = order.currency.value
  const selectedPaymentMethodConfig = getPaymentMethodConfig(order.paymentDetails.type, walletType)

  const sanitizePaypalMethod = (activePaymentMethodConfigs: PaymentMethodConfig[]) => {
    if (!Array.isArray(activePaymentMethodConfigs)) return []
    const isUSD = String(currency || '').toUpperCase() === 'USD'

    // We only need to know if PPC exists to prefer it over BRAINTREE in USD
    const hasPPC = activePaymentMethodConfigs.some(
      (m) => (m.processor || '').toUpperCase() === 'PAYPAL_COMMERCE',
    )

    return activePaymentMethodConfigs.filter((m) => {
      const proc = (m.processor || '').toUpperCase()

      if (proc === 'PAYPAL_COMMERCE') {
        // Non-USD: remove PPC (rule 2 & 3). USD: keep PPC.
        return isUSD
      }

      if (proc === 'BRAINTREE') {
        // USD: if PPC also exists, drop BRAINTREE (rule 1). Otherwise keep (rule 4).
        if (isUSD && hasPPC) return false
        return true // keep in non-USD (rule 2) or USD when PPC absent (rule 4)
      }

      // Leave other processors (e.g., STRIPE) untouched
      return true
    })
  }

  const paymentMethodConfigs = useActivePaymentMethodConfigs()

  const activePaymentMethodConfigs = sanitizePaypalMethod(paymentMethodConfigs)

  const trackUIElementEvent = useTrackUIElementEvent()

  // Helper function to get human-readable payment method name
  const getPaymentMethodDisplayName = useCallback(
    (paymentMethod: PaymentMethod): string => {
      const config = activePaymentMethodConfigs.find((config) => config.key === paymentMethod)

      return config?.label || paymentMethod
    },
    [activePaymentMethodConfigs],
  )

  // Initialize payment method state without triggering user interaction logic
  const initializePaymentMethod = useCallback(
    (value: PaymentMethod) => {
      const config = activePaymentMethodConfigs.find((config) => config.key === value)

      if (config && config.processor) {
        // Dispatch actions to set the payment type and processor
        order.paymentDetails.setType(value)
        order.paymentDetails.setProcessor(config.processor)

        // Track payment processor in analytics storage
        if (analytics && analytics.storage) {
          analytics.storage.setItem('paymentProcessor', config.processor)
        }
      } else {
        console.error(`No valid processor found for payment method: ${value}`)
      }
    },
    [activePaymentMethodConfigs, analytics, order.paymentDetails],
  )

  // Handle user selection of payment methods (actual user interactions only)
  const onSelect = useCallback(
    (value: PaymentMethod) => {
      const config = activePaymentMethodConfigs.find((config) => config.key === value)

      if (config && config.processor) {
        // Track every user interaction as a key donation event
        trackUIElementEvent('Donation Payment Method Selected', mParticleSdk.EventType.Other, {
          value: getPaymentMethodDisplayName(value),
        })

        // Dispatch actions to set the payment type and processor
        order.paymentDetails.setType(value)
        order.paymentDetails.setProcessor(config.processor)

        // Track payment processor in analytics storage
        if (analytics && analytics.storage) {
          analytics.storage.setItem('paymentProcessor', config.processor)
        }
      } else {
        console.error(`No valid processor found for payment method: ${value}`)
      }
    },
    [
      activePaymentMethodConfigs,
      trackUIElementEvent,
      getPaymentMethodDisplayName,
      order.paymentDetails,
      analytics,
    ],
  )

  // Initialize the selected payment method when configs are available
  // (without triggering user interaction logic)
  useEffect(() => {
    if (
      activePaymentMethodConfigs.length > 0 &&
      order.paymentDetails.type &&
      !hasInitializedRef.current
    ) {
      initializePaymentMethod(order.paymentDetails.type)
      hasInitializedRef.current = true
    }
  }, [activePaymentMethodConfigs.length, order.paymentDetails.type, initializePaymentMethod])

  return {
    onSelect,
    selectedPaymentMethod: order.paymentDetails.type,
    selectedPaymentMethodConfig,
    activePaymentMethodConfigs,
  }
}
